import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/** Shared legal-page layout */
function LegalPage({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-screen bg-bg text-white" data-testid={testId}>
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">{subtitle}</div>
            </div>
          </Link>
          <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5">
            <ArrowLeft size={14} /> Retour
          </Link>
        </div>
      </nav>
      <article className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-6">{title}</h1>
        <p className="text-white/60 text-sm mb-10">Dernière mise à jour : février 2026 · Version 1.0</p>
        <div className="prose-content space-y-8 text-white/80 leading-relaxed">{children}</div>
        <div className="mt-16 pt-8 border-t border-white/10 text-center text-xs text-white/40 flex flex-wrap justify-center gap-3">
          <Link to="/risk-disclosure" className="hover:text-white">Risques</Link><span>·</span>
          <Link to="/terms" className="hover:text-white">CGU</Link><span>·</span>
          <Link to="/privacy" className="hover:text-white">Confidentialité</Link><span>·</span>
          <Link to="/cookies" className="hover:text-white">Cookies</Link><span>·</span>
          <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link><span>·</span>
          <a href="mailto:legal@anatradex.com" className="hover:text-white">legal@anatradex.com</a>
        </div>
      </article>
    </div>
  );
}

export function Terms() {
  return (
    <LegalPage title="Conditions Générales d'Utilisation" subtitle="CGU" testId="terms-page">
      <section>
        <h2 className="font-heading text-2xl mb-3">1. Objet</h2>
        <p>Les présentes Conditions Générales d&apos;Utilisation (« CGU ») régissent l&apos;utilisation de la plateforme ANATRADEX (le « Service »), fournissant des outils d&apos;analyse quantitative et d&apos;apprentissage pour le trading sur les marchés financiers. En créant un compte, vous acceptez sans réserve ces CGU, la <Link to="/risk-disclosure" className="text-accent-blue underline">Déclaration de risques</Link> et la <Link to="/privacy" className="text-accent-blue underline">Politique de confidentialité</Link>.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">2. Éligibilité</h2>
        <ul className="space-y-1">
          <li>Vous devez avoir <b>au moins 18 ans</b> et la pleine capacité juridique.</li>
          <li>Vous ne devez pas être résident ou ressortissant d&apos;une juridiction où le Service est illégal.</li>
          <li>Vous ne devez pas être sous tutelle, curatelle ou souffrir de trouble du jeu.</li>
          <li>Une seule inscription par personne physique. La création de comptes multiples entraîne la suppression.</li>
        </ul>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">3. Nature du service</h2>
        <p>ANATRADEX est un <b>outil éducatif et analytique</b>. Il ne constitue pas :</p>
        <ul className="space-y-1 mt-2">
          <li>Un service de conseil en investissement au sens de la MiFID II (Directive 2014/65/UE)</li>
          <li>Un service de gestion de portefeuille</li>
          <li>Un broker, exchange ou intermédiaire financier</li>
          <li>Une garantie de performance ou de préservation du capital</li>
        </ul>
        <p className="mt-3">Toute décision de trading prise sur la base des analyses fournies relève de votre entière et exclusive responsabilité. Voir la <Link to="/risk-disclosure" className="text-accent-blue underline">Déclaration de risques</Link>.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">4. Comptes et abonnements</h2>
        <p>4.1 <b>Création de compte</b> — vous fournissez un email valide et un mot de passe. Vous êtes seul responsable de la confidentialité de vos identifiants.</p>
        <p>4.2 <b>Essai gratuit</b> — 14 jours sans engagement, sans carte bancaire requise. Passe automatiquement en tier gratuit si non converti.</p>
        <p>4.3 <b>Abonnements payants</b> — facturés mensuellement via Stripe. Renouvellement automatique. Résiliation possible à tout moment depuis les paramètres. Aucun remboursement au prorata sauf disposition légale contraire (délai de rétractation de 14 jours pour les consommateurs UE).</p>
        <p>4.4 <b>Prix</b> — indiqués sur la page <Link to="/pricing" className="text-accent-blue underline">Tarifs</Link>, susceptibles d&apos;évoluer avec préavis de 30 jours.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">5. Usage acceptable</h2>
        <p>Il vous est interdit de :</p>
        <ul className="space-y-1 mt-2">
          <li>Revendre, redistribuer, ou partager l&apos;accès à votre compte</li>
          <li>Extraire massivement les données par scraping ou automation non autorisée</li>
          <li>Contourner les limites de taux, quotas ou restrictions de tier</li>
          <li>Utiliser le Service pour du blanchiment, financement du terrorisme, ou toute activité illégale</li>
          <li>Reverse-engineerer, décompiler, ou tenter d&apos;extraire le code source</li>
          <li>Injecter du malware, spam, ou contenu offensant</li>
        </ul>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">6. Propriété intellectuelle</h2>
        <p>La marque ANATRADEX, les algorithmes propriétaires (ZII, DP, RL, CSD, CRT), le code, les designs et les contenus sont la propriété exclusive de leurs auteurs. Vous obtenez un droit d&apos;usage personnel, non exclusif, non cessible, révocable, limité à la durée de votre abonnement.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">7. Limitation de responsabilité</h2>
        <p>Dans les limites autorisées par la loi, ANATRADEX, ses fondateurs et affiliés ne sauraient être tenus responsables des pertes financières, directes ou indirectes, découlant de l&apos;usage du Service. La responsabilité contractuelle maximale est plafonnée aux 12 derniers mois d&apos;abonnement effectivement payés. Voir la <Link to="/risk-disclosure" className="text-accent-blue underline">Déclaration de risques</Link>.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">8. Suspension et résiliation</h2>
        <p>ANATRADEX peut suspendre ou résilier tout compte en cas de violation des CGU, fraude, comportement abusif ou non-paiement, sans préavis ni indemnité. Vous pouvez résilier à tout moment depuis les paramètres.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">9. Force majeure</h2>
        <p>ANATRADEX ne sera pas responsable de toute inexécution due à un cas de force majeure (panne DNS, cyberattaque, décision administrative, catastrophe naturelle, guerre, pandémie…).</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">10. Droit applicable & juridiction</h2>
        <p>Les présentes CGU sont régies par le droit français (ou, à défaut, le droit du pays de résidence de l&apos;utilisateur si consommateur UE). Tout litige sera porté devant les tribunaux compétents du lieu du siège social de l&apos;éditeur, sauf disposition impérative contraire du droit de la consommation.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">11. Contact</h2>
        <p>Pour toute question : <a href="mailto:legal@anatradex.com" className="text-accent-blue underline">legal@anatradex.com</a></p>
      </section>
    </LegalPage>
  );
}

export function Privacy() {
  return (
    <LegalPage title="Politique de confidentialité" subtitle="Privacy · RGPD" testId="privacy-page">
      <section>
        <h2 className="font-heading text-2xl mb-3">1. Responsable de traitement</h2>
        <p>ANATRADEX, contactable à <a href="mailto:privacy@anatradex.com" className="text-accent-blue underline">privacy@anatradex.com</a>, est responsable du traitement de vos données personnelles au sens du Règlement Général sur la Protection des Données (RGPD, Règlement UE 2016/679).</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">2. Données collectées</h2>
        <ul className="space-y-1">
          <li><b>Compte</b> — email, mot de passe (haché bcrypt), tier d&apos;abonnement, date de création</li>
          <li><b>Utilisation</b> — historique des signaux consultés, journaux de trade que vous saisissez volontairement, préférences (langue, devise, UI mode)</li>
          <li><b>Paiement</b> — via Stripe (nous ne stockons ni ne traitons vos données de carte bancaire — Stripe est PCI-DSS Level 1)</li>
          <li><b>Technique</b> — adresse IP, user-agent, timestamps de connexion (logs conservés 12 mois pour la sécurité)</li>
          <li><b>Broker</b> — si vous connectez OANDA/IBKR, la clé API est chiffrée en base ; vos ordres et positions transitent chez nous pour affichage</li>
        </ul>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">3. Bases légales & finalités (art. 6 RGPD)</h2>
        <ul className="space-y-1">
          <li><b>Exécution du contrat</b> — fournir le Service, gérer votre compte et paiements</li>
          <li><b>Intérêt légitime</b> — sécurité, prévention de fraude, amélioration produit, hash-chain d&apos;audit</li>
          <li><b>Consentement</b> — cookies analytiques non essentiels, communications marketing</li>
          <li><b>Obligation légale</b> — conservation comptable (10 ans), lutte anti-blanchiment (si applicable)</li>
        </ul>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">4. Vos droits (art. 15-22 RGPD)</h2>
        <p>Vous disposez des droits suivants, exerçables par email à <a href="mailto:privacy@anatradex.com" className="text-accent-blue underline">privacy@anatradex.com</a> :</p>
        <ul className="space-y-1 mt-2">
          <li>Droit d&apos;accès à vos données</li>
          <li>Droit de rectification</li>
          <li>Droit à l&apos;effacement (« droit à l&apos;oubli »)</li>
          <li>Droit à la portabilité (export JSON de vos données)</li>
          <li>Droit d&apos;opposition et de limitation</li>
          <li>Droit de retirer votre consentement à tout moment</li>
          <li>Droit d&apos;introduire une réclamation auprès d&apos;une autorité de contrôle (CNIL en France : cnil.fr)</li>
        </ul>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">5. Destinataires & transferts</h2>
        <p>Vos données peuvent être partagées avec les sous-traitants suivants, tous liés par contrat de conformité RGPD :</p>
        <ul className="space-y-1 mt-2">
          <li><b>Stripe</b> (paiements) — USA, Standard Contractual Clauses UE</li>
          <li><b>Resend</b> (emails transactionnels) — USA, DPA signé</li>
          <li><b>MongoDB Atlas</b> ou hébergement équivalent (base de données) — région UE privilégiée</li>
          <li><b>OANDA / IBKR</b> (brokers) — si vous connectez votre compte, sous leur propre politique de confidentialité</li>
          <li><b>Databento / Polygon / Twelve Data</b> (données de marché) — vos requêtes sont proxyfiées, aucune donnée personnelle transmise</li>
        </ul>
        <p className="mt-3">Aucun transfert hors UE sans garanties appropriées (Décisions d&apos;adéquation, Clauses Contractuelles Types).</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">6. Durée de conservation</h2>
        <ul className="space-y-1">
          <li>Compte actif : durée de la relation contractuelle</li>
          <li>Compte inactif : 3 ans après dernière connexion, puis suppression ou anonymisation</li>
          <li>Comptabilité : 10 ans (obligation légale)</li>
          <li>Logs de sécurité : 12 mois</li>
          <li>Hash-chain d&apos;audit : indéfini (immuabilité cryptographique — payloads sensibles redactés)</li>
        </ul>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">7. Sécurité</h2>
        <p>Nous mettons en œuvre des mesures techniques et organisationnelles conformes à l&apos;art. 32 RGPD : TLS 1.3, chiffrement des secrets, hachage bcrypt des mots de passe, row-level tenancy en base, rate-limiting, audit chain SHA-256 tamper-evident, MFA optionnel, logs d&apos;accès.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">8. Contact DPO</h2>
        <p><a href="mailto:privacy@anatradex.com" className="text-accent-blue underline">privacy@anatradex.com</a></p>
      </section>
    </LegalPage>
  );
}

export function Cookies() {
  return (
    <LegalPage title="Politique de cookies" subtitle="Cookies" testId="cookies-page">
      <section>
        <h2 className="font-heading text-2xl mb-3">Qu&apos;est-ce qu&apos;un cookie ?</h2>
        <p>Un cookie est un petit fichier texte stocké par votre navigateur lorsque vous visitez un site web. Il permet de mémoriser des informations entre les visites (session, préférences, statistiques).</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">Cookies utilisés par ANATRADEX</h2>
        <table className="w-full text-sm mt-3 border-collapse">
          <thead className="text-white/50 text-xs uppercase tracking-widest border-b border-white/10">
            <tr><th className="text-left py-2">Nom</th><th className="text-left py-2">Type</th><th className="text-left py-2">Finalité</th><th className="text-left py-2">Durée</th></tr>
          </thead>
          <tbody>
            <tr className="border-b border-white/5"><td className="py-2 font-mono text-xs">auth_token</td><td className="py-2">Essentiel</td><td className="py-2">Session authentifiée (JWT)</td><td className="py-2">24h</td></tr>
            <tr className="border-b border-white/5"><td className="py-2 font-mono text-xs">qtr-ui-mode</td><td className="py-2">Préférence</td><td className="py-2">Mode Simple/Avancé</td><td className="py-2">1 an</td></tr>
            <tr className="border-b border-white/5"><td className="py-2 font-mono text-xs">algo_lang</td><td className="py-2">Préférence</td><td className="py-2">Langue interface</td><td className="py-2">1 an</td></tr>
            <tr className="border-b border-white/5"><td className="py-2 font-mono text-xs">algo_currency</td><td className="py-2">Préférence</td><td className="py-2">Devise d&apos;affichage</td><td className="py-2">1 an</td></tr>
            <tr className="border-b border-white/5"><td className="py-2 font-mono text-xs">qtr-risk-ack</td><td className="py-2">Compliance</td><td className="py-2">Acceptation risque</td><td className="py-2">1 an</td></tr>
            <tr className="border-b border-white/5"><td className="py-2 font-mono text-xs">qtr-signup-ack</td><td className="py-2">Compliance</td><td className="py-2">Consentement inscription</td><td className="py-2">1 an</td></tr>
          </tbody>
        </table>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">Cookies tiers</h2>
        <p>Nous utilisons <b>Stripe</b> pour les paiements — Stripe dépose ses propres cookies pour la prévention de fraude. Voir la <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="text-accent-blue underline">politique Stripe</a>.</p>
        <p className="mt-3">Aucun cookie publicitaire, aucun tracker cross-site, aucun pixel Meta/Google Ads n&apos;est déposé sans votre consentement explicite.</p>
      </section>
      <section>
        <h2 className="font-heading text-2xl mb-3">Gestion des cookies</h2>
        <p>Les cookies essentiels ne peuvent pas être désactivés (nécessaires au fonctionnement). Vous pouvez supprimer tous les cookies depuis les paramètres de votre navigateur — cela vous déconnectera et réinitialisera vos préférences.</p>
      </section>
    </LegalPage>
  );
}

/**
 * Mentions légales — obligatoire en France (LCEN, article 6-III de la loi
 * n°2004-575 du 21 juin 2004). Doit contenir l'identité complète de l'éditeur,
 * l'hébergeur et les informations RCS/TVA si applicable. Les champs entre
 * crochets [...] doivent être remplis avec vos vraies données avant mise en prod.
 */
export function MentionsLegales() {
  return (
    <LegalPage title="Mentions légales" subtitle="Éditeur & Hébergeur" testId="mentions-legales-page">
      <section>
        <h2 className="font-heading text-2xl mb-3">1. Éditeur du site</h2>
        <p>Le site <b>ANATRADEX</b> (accessible via anatradex.com / anatradex.com / anatradex.trade) est édité par :</p>
        <ul className="space-y-1 mt-3">
          <li><b>Nom / raison sociale</b> : [À COMPLÉTER — nom complet ou dénomination sociale]</li>
          <li><b>Forme juridique</b> : [SASU / SARL / EI / Micro-entreprise — à préciser]</li>
          <li><b>Capital social</b> : [Montant en € si société — sinon supprimer]</li>
          <li><b>Siège social</b> : [Adresse complète]</li>
          <li><b>SIRET</b> : [14 chiffres]</li>
          <li><b>RCS</b> : [Ville d&apos;immatriculation, ex. Paris B 123 456 789]</li>
          <li><b>N° TVA intracommunautaire</b> : [FR XX XXX XXX XXX — si assujetti TVA]</li>
          <li><b>Directeur de la publication</b> : [Prénom Nom]</li>
          <li><b>Email de contact</b> : <a href="mailto:contact@anatradex.com" className="text-accent-blue underline">contact@anatradex.com</a></li>
          <li><b>Téléphone</b> : [Optionnel]</li>
        </ul>
      </section>

      <section>
        <h2 className="font-heading text-2xl mb-3">2. Hébergeur</h2>
        <p>Le site est hébergé par :</p>
        <ul className="space-y-1 mt-3">
          <li><b>Emergent Labs</b> (plateforme d&apos;hébergement Kubernetes managed)</li>
          <li><b>Site</b> : <a href="https://emergent.sh" target="_blank" rel="noreferrer" className="text-accent-blue underline">emergent.sh</a></li>
          <li><b>Infrastructure sous-jacente</b> : cloud providers tier-1 (AWS / GCP) — régions US/EU selon configuration</li>
        </ul>
        <p className="mt-3">Base de données : <b>MongoDB Atlas</b> — région UE privilégiée (Frankfurt ou Paris). Aucune donnée personnelle n&apos;est stockée hors zones à protection adéquate.</p>
      </section>

      <section>
        <h2 className="font-heading text-2xl mb-3">3. Propriété intellectuelle</h2>
        <p>L&apos;ensemble des éléments présents sur ANATRADEX (textes, logos, algorithmes propriétaires — <b>ZII, DP, RL, CSD, CRT</b> —, code source, illustrations, base de données, contenu de l&apos;Academy) est protégé par le droit d&apos;auteur et le droit des bases de données.</p>
        <p className="mt-3">Toute reproduction, représentation, adaptation, extraction, réutilisation partielle ou totale, par quelque procédé que ce soit, sans autorisation écrite préalable, est strictement interdite et constitue une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle.</p>
      </section>

      <section>
        <h2 className="font-heading text-2xl mb-3">4. Marques</h2>
        <p>La marque <b>ANATRADEX</b>, son logo et sa signature graphique (icône prisme + spectre décomposé) [sont / feront l&apos;objet d&apos;un] dépôt à l&apos;INPI et à l&apos;EUIPO. Toute utilisation non autorisée est passible de poursuites au titre de l&apos;article L.713-2 du Code de la propriété intellectuelle.</p>
      </section>

      <section>
        <h2 className="font-heading text-2xl mb-3">5. Loi applicable</h2>
        <p>Les présentes mentions légales et l&apos;utilisation du site ANATRADEX sont régies par la loi française. En cas de litige, et à défaut d&apos;accord amiable, la compétence est expressément attribuée aux tribunaux français, sauf disposition impérative contraire du droit européen de la consommation.</p>
      </section>

      <section>
        <h2 className="font-heading text-2xl mb-3">6. Statut réglementaire</h2>
        <p>ANATRADEX est un outil <b>éducatif et analytique</b>. Il ne fournit <b>pas</b> de conseil en investissement et n&apos;est <b>pas</b> une entreprise d&apos;investissement au sens de la Directive MiFID II (Directive 2014/65/UE), ni un prestataire de services d&apos;investissement (PSI) enregistré auprès de l&apos;AMF ou de l&apos;ACPR.</p>
        <p className="mt-3">Voir la <Link to="/risk-disclosure" className="text-accent-blue underline">Déclaration de risques</Link> et les <Link to="/terms" className="text-accent-blue underline">CGU</Link> pour le détail du cadre d&apos;usage.</p>
      </section>

      <section>
        <h2 className="font-heading text-2xl mb-3">7. Signalement de contenu illicite</h2>
        <p>Conformément à l&apos;article 6-I-5 de la LCEN, tout contenu apparaissant illicite peut être signalé à <a href="mailto:legal@anatradex.com" className="text-accent-blue underline">legal@anatradex.com</a>. Nous nous engageons à agir promptement pour retirer les contenus manifestement illicites qui nous sont notifiés.</p>
      </section>

      <section>
        <h2 className="font-heading text-2xl mb-3">8. Médiation de la consommation</h2>
        <p>Conformément aux articles L.611-1 et suivants du Code de la consommation, en cas de litige non résolu à l&apos;amiable, le consommateur peut recourir gratuitement au service de médiation suivant : [À DÉSIGNER — ex. CNPM, Médiation Consommation, medicys.fr]. Plateforme européenne de règlement en ligne des litiges : <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" className="text-accent-blue underline">ec.europa.eu/consumers/odr</a>.</p>
      </section>
    </LegalPage>
  );
}
